import { NgClass } from '@angular/common';
import { Component, HostListener, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { Menu } from '@core/models/menu.model';
import { EnvironmentService } from '@core/services/environment/environment.service';
import { PurchaseWidgetService } from '@core/services/purchase-widget/purchase-widget.service';
import { SessionService } from '@core/services/session/session.service';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { Divider } from 'primeng/divider';
import { DrawerModule } from 'primeng/drawer';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Skeleton } from 'primeng/skeleton';
import { StyleClassModule } from 'primeng/styleclass';
import { BadgeModule } from 'primeng/badge';
import { LoggerService } from '@core/services/logger/logger.service';
import { AuthStore } from '@features/authentication/state/auth.store';
import {
  LayoutThemeColorType,
  LayoutThemeService,
  LayoutThemeSurface,
} from '@core/services/layout/layout-theme.service';

@Component({
  selector: 'd2d-header',
  standalone: true,
  imports: [
    NgClass,
    RouterLink,
    FormsModule,
    SelectModule,
    ButtonModule,
    DrawerModule,
    AvatarModule,
    StyleClassModule,
    RippleModule,
    Divider,
    SelectButtonModule,
    Skeleton,
    RouterLinkActive,
    TranslocoPipe,
    BadgeModule,
  ],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  private readonly router = inject(Router);
  private readonly layoutThemeService = inject(LayoutThemeService);
  private readonly sessionService = inject(SessionService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly logger = inject(LoggerService).forContext('Header');
  readonly purchaseWidgetService = inject(PurchaseWidgetService);
  readonly authStore = inject(AuthStore);
  public bgWhite = input(true);
  trackingNumber = '';
  isMenuOpen = false;
  hideHeader = false;
  lastScrollPosition = 0;
  currentActionScroll = 500;
  colorOptions = this.layoutThemeService.colorOptions;
  selectedColor = this.layoutThemeService.selectedPrimaryColor();
  darkTheme = this.layoutThemeService.darkTheme;

  // Signals del AuthStore
  sessionState = this.authStore.sessionState;
  account = this.authStore.currentAccount;

  get isDark(): boolean {
    return this.darkTheme();
  }

  showAvatar() {
    const currentAccount = this.account();
    if (currentAccount) {
      currentAccount.photo_url = '';
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const currentScrollPosition = window.scrollY || document.documentElement.scrollTop;

    // Mostramos header cuando hacemos scroll hacia arriba y lo ocultamos cuando hacemos scroll hacia abajo
    this.hideHeader =
      currentScrollPosition > this.currentActionScroll &&
      currentScrollPosition > this.lastScrollPosition;

    // Actualizamos la última posición de scroll
    this.lastScrollPosition = currentScrollPosition;
  }

  menu: Menu[] = [
    {
      title: 'header.nav.home',
      url: 'home',
      type: 'link',
    },
    {
      title: 'header.nav.services',
      url: 'services',
      type: 'link',
    },
    {
      title: 'header.nav.aboutUs',
      url: 'about-us',
      type: 'link',
    },
    {
      title: 'header.nav.tracking',
      url: 'tracking',
      type: 'link',
    },
  ];

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  goToLogin() {
    this.router.navigate(['/auth/login']);
  }

  toggleDarkMode() {
    this.layoutThemeService.toggleDarkMode({ syncMenuTheme: false });
  }

  logout() {
    this.sessionService.logout();
  }

  changeAccount() {
    this.router.navigate(['/auth/change-account']);
  }

  get hasAccounts(): boolean {
    return this.authStore.hasAccounts();
  }

  goToAdmin() {
    const currentAccount = this.account();
    if (
      currentAccount &&
      currentAccount.roles &&
      currentAccount.roles.length > 0 &&
      this.environmentService.isBrowser()
    ) {
      window.open(this.environmentService.ADMIN_URL, '_blank');
    } else {
      this.logger.warn('Acción no permitida: el usuario no tiene permisos para esta acción.');
    }
  }

  searchTracking() {
    const number = this.trackingNumber.trim();
    if (!number) return;

    this.closeSidebar(); // <- cerrar antes de navegar (o hazlo en el .then)

    this.router.navigate(['/tracking', number]).then(() => {
      this.trackingNumber = '';
    });
  }

  goToTracking() {
    this.closeSidebar();
    this.router.navigate(['/tracking']);
  }

  private closeSidebar() {
    this.isMenuOpen = false;
  }

  updateColors(type: LayoutThemeColorType, color: LayoutThemeSurface | string, event?: Event) {
    this.layoutThemeService.updateColors(type, color, event);
  }
}
